/**
 * @fileoverview Job Overview Summary — AI-generated "mini wiki" background
 * @module components/jobs/phases/overview/JobOverviewSummary
 *
 * MOR-485 — Renders the 2-paragraph (≤400 word) business background card on
 * the job overview page. Three concerns:
 *   - **View**: read-only sanitised HTML.
 *   - **Edit**: TipTap rich editor (NotesEditor) with autosave on save.
 *   - **Chat**: uses the shared premium `ChatSheet` slide-out (same chrome
 *     as every other agent chat in the product) backed by my Managed Agent
 *     chat table.
 *
 * The card itself is a `CollapsibleCard` so it matches the rest of the
 * overview page chrome.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import {
  Loader2,
  Pencil,
  RefreshCw,
  MessageSquare,
  Sparkles,
  History,
  X,
  ChevronDown,
  ChevronRight,
  Bot,
  User as UserIcon,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { CollapsibleCard } from "./CollapsibleCard";
import { CopyableValue } from "./CopyableValue";
import { convertHtmlToCopyText } from "./html-to-copy-text";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

interface JobOverviewSummaryProps {
  jobId: Id<"jobs">;
}

export function JobOverviewSummary({ jobId }: JobOverviewSummaryProps) {
  const { data: summary, isLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .getOverviewSummary,
      { jobId },
    ),
  );

  const [mode, setMode] = useState<"view" | "edit">("view");
  const [chatOpen, setChatOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  if (isLoading || !summary) {
    return (
      <CollapsibleCard
        icon={Sparkles}
        title="Background"
        defaultOpen
        persistId="overview:background"
        actions={null}
      >
        <div className="flex items-center gap-2 px-5 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading background…
        </div>
      </CollapsibleCard>
    );
  }

  const taskRunning = summary.taskStatus === "running";
  const hasSummary = Boolean(
    summary.summaryHtml && summary.summaryHtml.trim().length > 0,
  );

  return (
    <>
      <CollapsibleCard
        icon={Sparkles}
        title="Background"
        defaultOpen
        persistId="overview:background"
        statusSlot={
          taskRunning ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Working…
            </span>
          ) : undefined
        }
        actions={
          <Actions
            jobId={jobId}
            canEdit={summary.canEdit}
            mode={mode}
            hasSummary={hasSummary}
            taskRunning={taskRunning}
            historyOpen={historyOpen}
            onEdit={() => setMode("edit")}
            onChat={() => setChatOpen(true)}
            onToggleHistory={() => setHistoryOpen((v) => !v)}
          />
        }
      >
        {mode === "edit" ? (
          <EditPanel
            jobId={jobId}
            initialHtml={summary.summaryHtml ?? ""}
            onDone={() => setMode("view")}
          />
        ) : (
          <ViewPanel
            summaryHtml={summary.summaryHtml}
            agentUpdatedAt={summary.agentUpdatedAt}
            userEditedAt={summary.userEditedAt}
            taskRunning={taskRunning}
            hasSummary={hasSummary}
          />
        )}
        {historyOpen && <HistoryPanel jobId={jobId} />}
      </CollapsibleCard>

      <OverviewChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        jobId={jobId}
        canSend={summary.canEdit}
        agentBusy={taskRunning}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Card header actions — Edit / Regenerate / Chat
// ---------------------------------------------------------------------------

function Actions(props: {
  jobId: Id<"jobs">;
  canEdit: boolean;
  mode: "view" | "edit";
  hasSummary: boolean;
  taskRunning: boolean;
  historyOpen: boolean;
  onEdit: () => void;
  onChat: () => void;
  onToggleHistory: () => void;
}) {
  const {
    jobId,
    canEdit,
    mode,
    hasSummary,
    taskRunning,
    historyOpen,
    onEdit,
    onChat,
    onToggleHistory,
  } = props;

  const { mutateAsync: regenerate, isPending: regenPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .regenerateOverviewNow,
    ),
  });

  const handleRegenerate = async () => {
    try {
      await regenerate({ jobId });
      toast.success("Regenerating background summary…");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to regenerate summary",
      );
    }
  };

  // Stop the row's button clicks from bubbling into the Collapsible header,
  // which would otherwise toggle the card collapsed/expanded.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="flex items-center gap-1" onClick={stop}>
      {canEdit && mode === "view" && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          disabled={!hasSummary && taskRunning}
          title="Edit the background summary by hand"
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      )}
      {canEdit && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5"
          onClick={(e) => {
            e.stopPropagation();
            void handleRegenerate();
          }}
          disabled={regenPending || taskRunning}
          title="Re-run the agent to refresh the summary"
        >
          <RefreshCw
            className={
              regenPending || taskRunning
                ? "h-3.5 w-3.5 animate-spin"
                : "h-3.5 w-3.5"
            }
          />
          {hasSummary ? "Regenerate" : "Generate"}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5"
        onClick={(e) => {
          e.stopPropagation();
          onChat();
        }}
        title="Chat with the agent to refine the summary"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        Chat
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={historyOpen ? "gap-1.5 bg-muted text-foreground" : "gap-1.5"}
        onClick={(e) => {
          e.stopPropagation();
          onToggleHistory();
        }}
        title="Show edit and regeneration history"
        aria-pressed={historyOpen}
      >
        <History className="h-3.5 w-3.5" />
        History
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View panel
// ---------------------------------------------------------------------------

function ViewPanel(props: {
  summaryHtml?: string;
  agentUpdatedAt?: number;
  userEditedAt?: number;
  taskRunning: boolean;
  hasSummary: boolean;
}) {
  const { summaryHtml, agentUpdatedAt, userEditedAt, taskRunning, hasSummary } =
    props;

  const safeHtml = useMemo(
    () => sanitizeSummaryHtml(summaryHtml ?? ""),
    [summaryHtml],
  );

  if (!hasSummary) {
    return (
      <div className="px-5 py-6 text-sm text-muted-foreground">
        {taskRunning ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Drafting a background summary from this job's documents…
          </span>
        ) : (
          <p>
            No background summary yet. Once relevant documents finish indexing
            the agent will generate a short factual overview of the business —
            or click <strong>Generate</strong> above to run it now.
          </p>
        )}
      </div>
    );
  }

  const lastEdit = userEditedAt ?? 0;
  const lastAgent = agentUpdatedAt ?? 0;
  const lastSource: "user" | "agent" | null =
    lastEdit > lastAgent ? "user" : lastAgent > 0 ? "agent" : null;
  const lastAt = Math.max(lastEdit, lastAgent);
  const copyText = convertHtmlToCopyText(safeHtml);

  return (
    <CopyableValue
      value={copyText}
      label="background"
      placement="overlay"
      className="space-y-3 px-5 py-4"
    >
      <div
        className="prose prose-sm max-w-none pr-8 text-foreground/90 dark:prose-invert"
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- safeHtml comes from sanitizeSummaryHtml (DOMPurify) in the memo above
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
      {lastSource && (
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {lastSource === "user" ? "Edited by you" : "Generated by agent"} ·{" "}
          {formatTimeAgo(lastAt)}
        </p>
      )}
      <AiDisclaimer />
    </CopyableValue>
  );
}

// ---------------------------------------------------------------------------
// Edit panel — TipTap autosave
// ---------------------------------------------------------------------------

function EditPanel(props: {
  jobId: Id<"jobs">;
  initialHtml: string;
  onDone: () => void;
}) {
  const { jobId, initialHtml, onDone } = props;
  const [draftHtml, setDraftHtml] = useState(initialHtml);
  const editorRef = useRef<NotesEditorHandle | null>(null);

  const { mutateAsync: saveSummary, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .saveOverviewSummary,
    ),
  });

  const handleSave = async () => {
    editorRef.current?.flush();
    const html = draftHtml.trim();
    if (!html) {
      toast.error("Summary cannot be empty");
      return;
    }
    const text = stripHtml(html);
    const wordCount = countWords(text);
    if (wordCount > 600) {
      toast.error(
        `Summary exceeds the 400-word ceiling (currently ${wordCount} words).`,
      );
      return;
    }
    try {
      await saveSummary({ jobId, summaryHtml: html, summaryText: text });
      toast.success("Background updated");
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    }
  };

  return (
    <div className="space-y-3 px-4 py-4">
      <NotesEditor
        ref={editorRef}
        value={draftHtml}
        onChange={setDraftHtml}
        minHeightClass="min-h-[180px]"
        placeholder="Two short paragraphs covering what the business does, when it started, where it operated, and the nature of its operations."
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          Target: 2 paragraphs, ≤400 words. The agent respects your edits on
          future runs.
        </p>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDone}
            disabled={isPending}
            className="gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={isPending}
            className="gap-1"
          >
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History — inline list of edits (user + agent), click to preview snapshot
// ---------------------------------------------------------------------------

function HistoryPanel({ jobId }: { jobId: Id<"jobs"> }) {
  const { data: entries, isLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .listOverviewSummaryHistory,
      { jobId },
    ),
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="border-t border-border bg-muted/30">
      <div className="flex items-center gap-2 px-5 py-3">
        <History className="h-3.5 w-3.5 text-muted-foreground" />
        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          History
        </h5>
      </div>
      {isLoading ? (
        <div className="px-5 pb-4 text-xs text-muted-foreground">
          Loading history…
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="px-5 pb-4 text-xs text-muted-foreground">
          No edits yet. Agent runs and your manual edits will appear here.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {entries.map((e) => {
            const isExpanded = expandedId === e.id;
            const actorLabel =
              e.kind === "user_edit" ? (actorName(e.actor) ?? "User") : "Agent";
            return (
              <li key={e.id} className="px-5 py-2">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left text-xs transition-colors hover:text-foreground"
                  onClick={() => setExpandedId(isExpanded ? null : e.id)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                  {e.kind === "user_edit" ? (
                    <UserIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
                  ) : (
                    <Bot className="h-3 w-3 shrink-0 text-primary" />
                  )}
                  <span className="font-medium text-foreground/90">
                    {actorLabel}
                  </span>
                  <span className="text-muted-foreground">
                    {e.kind === "user_edit"
                      ? "edited"
                      : "regenerated the summary"}
                  </span>
                  <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                    {formatHistoryTime(e.at)}
                  </span>
                </button>
                {isExpanded && (
                  <div className="ml-5 mt-2 space-y-2">
                    {e.changeRationale && e.kind === "agent_run" && (
                      <p className="text-xs italic text-muted-foreground">
                        “{e.changeRationale}”
                      </p>
                    )}
                    {e.summaryHtml ? (
                      <div className="rounded-md border border-border bg-background p-3">
                        <div
                          className="prose prose-sm max-w-none text-xs text-foreground/85 dark:prose-invert"
                          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- sanitizeSummaryHtml (DOMPurify) applied inline
                          dangerouslySetInnerHTML={{
                            __html: sanitizeSummaryHtml(e.summaryHtml),
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No snapshot was captured for this entry.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function actorName(actor?: {
  firstName?: string;
  lastName?: string;
  email: string;
}): string | undefined {
  if (!actor) return undefined;
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(" ");
  return name || actor.email;
}

function formatHistoryTime(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Chat — wraps the shared ChatSheet primitive
// ---------------------------------------------------------------------------

function OverviewChatSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: Id<"jobs">;
  canSend: boolean;
  agentBusy: boolean;
}) {
  const { open, onOpenChange, jobId, canSend, agentBusy } = props;
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const { data: chatMessages } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .listOverviewChatMessages,
      { jobId },
    ),
  );

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.jobOverview.publicMutations
        .sendOverviewChatMessage,
    ),
  });

  const realMessages: ChatMessageData[] = useMemo(() => {
    return (chatMessages ?? [])
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        id: m._id,
        role: m.role as "user" | "assistant",
        content: m.body,
        createdAt: m.createdAt,
      }));
  }, [chatMessages]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Clear the optimistic message once it lands in the real list.
  useEffect(() => {
    if (!pendingMessage) return;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  // Show the thinking indicator when the last message is from the user (the
  // agent run has been scheduled but hasn't replied yet) or while a run is in
  // progress against this job.
  const lastMessage = messages[messages.length - 1];
  const isThinking =
    agentBusy ||
    isPending ||
    (lastMessage?.role === "user" && pendingMessage !== null) ||
    (lastMessage?.role === "user" && agentBusy);

  const handleSend = async (raw: string) => {
    const message = raw.trim();
    if (!message || !canSend) return;
    setPendingMessage(message);
    try {
      await sendMessage({ jobId, message });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
      setPendingMessage(null);
    }
  };

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Background"
      description="Refine the AI-generated business overview. The agent re-emits the full summary in place; manual edits to the editor are preserved."
      messages={messages}
      onSend={(m) => void handleSend(m)}
      userInfo={userInfo}
      isSending={isPending}
      isThinking={isThinking}
      emptyMessage="Ask the agent to refine the background — for example 'the business is a wholesaler, not a retailer' or 'include that they exported to New Zealand'."
      inputPlaceholder={
        canSend
          ? "Tell the agent what to change…"
          : "Viewers can't chat with the agent"
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripHtml(html: string): string {
  if (typeof window === "undefined") {
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const el = document.createElement("div");
  el.innerHTML = DOMPurify.sanitize(html);
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

function countWords(text: string): number {
  return text.split(/\s+/).filter((w) => w.length > 0).length;
}

function formatTimeAgo(ms: number): string {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

/**
 * Sanitise summary HTML before rendering — the source is either the TipTap
 * editor (constrained) or the AI agent (untrusted). DOMPurify strips anything
 * outside the allow-list to prevent stored-XSS via a coerced agent output.
 */
function sanitizeSummaryHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "strong", "em", "u", "a", "ul", "ol", "li", "br"],
    ALLOWED_ATTR: ["href", "rel", "target"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
  });
}
